'use client';

import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { Box, Divider, Link, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { ReactNode } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ComponentBlocksAuthorDetails } from '@/types';

function externalHref(url: string) {
  try {
    const parsedUrl = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    return parsedUrl.protocol === 'https:' ? parsedUrl.toString() : null;
  } catch {
    return null;
  }
}

interface AuthorCardSidebarProps extends ComponentBlocksAuthorDetails {
  fixedSidebar?: boolean;
}

export function AuthorCardSidebar({
  WrittenBy,
  ShowBio = false,
  ShowSocialLabels = true,
  fixedSidebar = false,
}: AuthorCardSidebarProps) {
  const theme = useTheme();
  const { Name, Role, Bio, Avatar, LinkedInUrl, Email, FacebookUrl, InstagramUrl, XUrl } =
    WrittenBy || {};
  const facebookHref = FacebookUrl ? externalHref(FacebookUrl) : null;
  const instagramHref = InstagramUrl ? externalHref(InstagramUrl) : null;
  const xHref = XUrl ? externalHref(XUrl) : null;
  const linkedInHref = LinkedInUrl ? externalHref(LinkedInUrl) : null;
  const socialLinks = [
    Email && {
      label: 'Mail',
      href: `mailto:${Email}`,
      icon: <EmailIcon />,
    },
    facebookHref && {
      label: 'Facebook',
      href: facebookHref,
      icon: <FacebookIcon />,
    },
    instagramHref && {
      label: 'Instagram',
      href: instagramHref,
      icon: <InstagramIcon />,
    },
    xHref && {
      label: 'X Twitter',
      ariaLabel: 'X Twitter',
      href: xHref,
      icon: <XIcon />,
    },
    linkedInHref && {
      label: 'LinkedIn',
      href: linkedInHref,
      icon: <LinkedInIcon />,
    },
  ].filter(Boolean) as Array<{
    ariaLabel?: string;
    href: string;
    icon: ReactNode;
    label: string;
  }>;

  return (
    <Box
      component="aside"
      aria-label={`About ${Name || 'the author'}`}
      sx={{
        borderRadius: 3,
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
        p: fixedSidebar ? { xs: 2, md: 2.5 } : { xs: 3, md: 3.5 },
        textAlign: 'center',
      }}
    >
      {getImageUrl({ image: Avatar, size: 'small' }) && (
        <Box mb={1.5}>
          <Image
            src={getImageUrl({ image: Avatar, size: 'small' })!}
            alt={Avatar?.alternativeText || Name || 'Author'}
            width={56}
            height={56}
            style={{ borderRadius: '50%', objectFit: 'cover' }}
          />
        </Box>
      )}

      {Name && (
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            color: theme.palette.brand.primary,
            fontSize: { xs: 18, sm: 20 },
          }}
        >
          {Name}
        </Typography>
      )}

      {Role && (
        <Typography
          sx={{
            mb: socialLinks.length ? 2.5 : 0,
            color: theme.palette.text.secondary,
            fontSize: { xs: 14, sm: 15 },
          }}
        >
          {Role}
        </Typography>
      )}

      {ShowBio && Bio && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              mb: socialLinks.length ? 2.5 : 0,
            }}
          >
            {Bio}
          </Typography>
          {!!socialLinks.length && <Divider sx={{ mb: 2.5 }} />}
        </>
      )}

      {!!socialLinks.length && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            columnGap: { xs: 2.5, md: 3 },
            rowGap: 1.5,
          }}
        >
          {socialLinks.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              target={item.label === 'Mail' ? undefined : '_blank'}
              rel={item.label === 'Mail' ? undefined : 'noopener noreferrer'}
              aria-label={item.ariaLabel ?? item.label}
              underline="none"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: ShowSocialLabels ? 56 : 28,
                gap: ShowSocialLabels ? 0.75 : 0,
                color: theme.palette.brand.primarySecondary1,
                fontSize: 12,
                lineHeight: 1.2,
                '& svg': {
                  fontSize: 22,
                },
              }}
            >
              {item.icon}
              {ShowSocialLabels && item.label}
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
}
